/*
 * Copyright 2026 Sony Corporation
 */

import { Color3 } from '@babylonjs/core/Maths/math.color';
import { GridMaterial } from '@babylonjs/materials/grid/gridMaterial';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';

import { Disposables } from './disposables';
import { createSceneInstance } from './scene-instance';
import { snapshotGlobals } from './globals';
import type { SceneSource, StagedScene } from './types';

const EXTENT = 20;

/**
 * The empty state: what a world renders before a scene has been built for it,
 * and what a failed load falls back to.
 *
 * Nothing ships with the deployment, so this is not a scene anyone can be bound
 * to — it is drawn here, from nothing. It has no assets at all, which is the
 * point: it is what renders when S3, CloudFront and the API are unreachable,
 * and a fallback that needs the network is not a fallback.
 */
export const grid: SceneSource = {
  id: 'grid',
  name: 'Grid',

  stage: async ({ scene }): Promise<StagedScene> => {
    const own = new Disposables();
    const globals = snapshotGlobals(scene);

    const root = new TransformNode('GridRoot', scene);
    const ground = MeshBuilder.CreateGround('GridGround', { width: EXTENT * 2, height: EXTENT * 2 }, scene);
    const material = new GridMaterial('GridMaterial', scene);

    material.majorUnitFrequency = 5;
    material.minorUnitVisibility = 0.4;
    material.gridRatio = 1;
    material.mainColor = new Color3(0.09, 0.11, 0.14);
    material.lineColor = new Color3(0.35, 0.42, 0.5);
    material.opacity = 0.99;

    ground.material = material;
    ground.parent = root;
    ground.receiveShadows = true;
    ground.isPickable = false;

    // A procedural scene has nothing to download, so it is hidden rather than
    // held in an AssetContainer; loaded scenes stage detached instead.
    root.setEnabled(false);
    own.add(material, ground, root);

    return {
      commit: () => {
        root.setEnabled(true);
        return createSceneInstance({
          sceneId: grid.id,
          scene,
          root,
          bounds: { center: Vector3.Zero(), radius: EXTENT },
          defaultView: { alpha: -Math.PI / 2, beta: 1.05, radius: EXTENT * 1.6 },
          own,
          globals,
        });
      },
      abandon: () => own.dispose(),
    };
  },
};
